/**
 * mediaLibraryVmBuilder — traduz o estado da biblioteca (resposta de `GET /api/media` + envios + escolha) no ViewModel
 * do contrato `mediaLibrary.vm.js` @v1.
 *
 * Função PURA de propósito: cota, quem pode ser escolhido, rótulos e mensagens são decididos aqui, testáveis sem React.
 * O `useMediaLibrary` cuida de estado, efeitos e ações.
 *
 * Item da Mídia (camelCase, ver Blasoft.Agendario.Media `ItemDaBiblioteca`):
 *   { assetId, origem (0 dono | 1 exemplo), w, h, color, alt, nome, credito, criadoEm, usadoEm, miniatura, previa }
 */
import { MediaOrigin } from '../../catalogs/pageCatalog.generated.js';

export const MAX_FOTOS_POR_GALERIA = 30;      // SiteConfigValidator.MaxItensDaGaleria (Api) — M32
export const MAX_DESCRICAO = 250;             // SiteConfigValidator.MaxDescricao / Mídia (M12)

const plural = (n, um, varios) => (n === 1 ? um : varios);

/** Erros cujo motivo passa sozinho (rede, sistema, ritmo) — vale tentar de novo. Os outros dependem do dono. */
const CODIGOS_QUE_VALE_TENTAR = new Set(['REDE', 'ERRO', 'MIDIA_INDISPONIVEL', 'FILESERVICE_INDISPONIVEL', 'MUITOS_PEDIDOS', 'EM_ENVIO']);

export function montarCota(cota) {
  const usadas = Math.max(0, Number(cota?.usadas) || 0);
  const limite = cota?.limite ?? null;
  if (limite === null || limite === undefined) {
    return { label: `${usadas} ${plural(usadas, 'foto', 'fotos')}`, used: usadas, limit: null, percent: null, level: 'ok', hint: null };
  }
  const restam = Math.max(0, limite - usadas);
  const level = restam === 0 ? 'full' : usadas >= limite * 0.8 ? 'near' : 'ok';
  return {
    label: `${usadas} de ${limite} fotos`,
    used: usadas,
    limit: limite,
    percent: limite > 0 ? Math.min(100, Math.round((usadas * 100) / limite)) : 100,
    level,
    hint: level === 'full'
      ? 'Biblioteca cheia: apague alguma ou mude de plano.'
      : level === 'near' ? `${plural(restam, 'Falta', 'Faltam')} ${restam} ${plural(restam, 'foto', 'fotos')} para encher.` : null,
  };
}

const ROTULO_DA_ETAPA = {
  waiting: () => 'Na fila',
  preparing: () => 'Preparando',
  uploading: (e) => `Enviando ${Math.round(e.progresso || 0)}%`,
  processing: () => 'Processando',
  done: () => 'Pronta',
  duplicate: () => 'Você já tem esta foto',
};

export function montarEnvio(e) {
  const state = e.etapa;
  const erro = state === 'error';
  return {
    id: e.id,
    fileName: e.nome,
    previewUrl: e.previewUrl ?? null,
    state,
    progress: state === 'uploading' ? Math.round(e.progresso || 0) : state === 'done' || state === 'processing' ? 100 : 0,
    statusLabel: erro ? (e.erro?.mensagem || 'Não deu certo.') : ROTULO_DA_ETAPA[state](e),
    tone: erro ? 'error' : state === 'done' ? 'success' : 'neutral',
    canRetry: erro && CODIGOS_QUE_VALE_TENTAR.has(e.erro?.codigo),
    canDismiss: erro || state === 'done' || state === 'duplicate',
  };
}

/**
 * @param {object} s estado do hook:
 *   open, mode ('gallery'|'logo'), carregando, erro ({mensagem}|null), itens (da Mídia), proximo, cota, carregandoMais,
 *   envios, selecao (assetIds na ordem do toque), jaNaGaleria (assetIds), vagas (quantas cabem na galeria), logoAtual,
 *   nomeDoRamo, apagando (assetIds), descricao ({aberta,itemId,valor,salvando,erro}),
 *   apagar ({aberto,itemId,emUso,apagando,erro}), aviso ({tone,message}|null), telaCheia
 */
export function montarVm(s) {
  const galeria = s.mode !== 'logo';
  const itens = s.itens ?? [];
  const selecao = s.selecao ?? [];
  const jaNaGaleria = new Set(s.jaNaGaleria ?? []);
  const apagando = new Set(s.apagando ?? []);
  const vagas = galeria ? Math.max(0, s.vagas ?? MAX_FOTOS_POR_GALERIA) : null;
  const cota = montarCota(s.cota);
  const porId = new Map(itens.map((i) => [i.assetId, i]));

  const item = (i) => {
    const doDono = i.origem === MediaOrigin.Owner;
    const posicao = selecao.indexOf(i.assetId);
    const selected = posicao >= 0;
    const naGaleria = galeria && jaNaGaleria.has(i.assetId);
    const usadoEm = Number(i.usadoEm) || 0;
    return {
      id: i.assetId,
      origin: doDono ? 'owner' : 'example',
      thumbnailUrl: i.miniatura,
      previewUrl: i.previa,
      aspectRatio: i.w > 0 && i.h > 0 ? i.w / i.h : 1,
      color: i.color ?? null,
      alt: i.alt ?? '',
      name: doDono ? (i.nome ?? null) : null,
      badgeLabel: doDono ? null : 'Exemplo',
      creditLabel: !doDono && i.credito ? `Foto: ${i.credito}` : null,
      usedInLabel: usadoEm > 0 ? `Em ${usadoEm} ${plural(usadoEm, 'página', 'páginas')}` : null,
      addedLabel: naGaleria ? 'Já está nesta galeria' : !galeria && i.assetId === s.logoAtual ? 'Logo atual' : null,
      selected,
      selectionIndex: galeria && selected ? posicao + 1 : null,
      selectable: galeria ? !naGaleria && (selected || selecao.length < vagas) : true,
      canDescribe: doDono && !apagando.has(i.assetId),
      canDelete: doDono && !apagando.has(i.assetId),
      busy: apagando.has(i.assetId),
    };
  };

  const doDono = itens.filter((i) => i.origem === MediaOrigin.Owner);
  const sections = [{
    key: 'owner',
    title: 'Suas fotos',
    subtitle: null,
    countLabel: doDono.length ? String(doDono.length) : null,
    emptyMessage: doDono.length ? null : galeria
      ? 'Você ainda não enviou fotos. As suas aparecem aqui, antes dos exemplos.'
      : 'Envie a imagem do seu logo — de preferência quadrada e com fundo.',
    items: doDono.map(item),
  }];
  if (galeria) {
    sections.push({
      key: 'examples',
      title: s.nomeDoRamo ? `Exemplos de ${s.nomeDoRamo}` : 'Exemplos',
      subtitle: 'Fotos de banco de imagens para começar — troque pelas suas quando puder.',
      countLabel: null,
      emptyMessage: null,
      items: itens.filter((i) => i.origem !== MediaOrigin.Owner).map(item),
    });
  }

  const count = selecao.length;
  const restam = galeria ? vagas - count : null;
  const selection = galeria
    ? {
      count,
      max: vagas,
      label: count === 0 ? 'Nenhuma foto escolhida' : `${count} ${plural(count, 'foto escolhida', 'fotos escolhidas')}`,
      limitLabel: vagas === 0
        ? `Esta galeria já tem ${MAX_FOTOS_POR_GALERIA} fotos, o limite.`
        : restam <= 0 ? `Limite de ${MAX_FOTOS_POR_GALERIA} fotos por galeria` : restam <= 5 ? `Cabem mais ${restam} nesta galeria` : null,
      limitReached: restam <= 0,
      confirmLabel: count === 0 ? 'Adicionar fotos' : `Adicionar ${count} ${plural(count, 'foto', 'fotos')}`,
      canConfirm: count > 0,
    }
    : {
      count,
      max: null,
      label: count ? 'Logo escolhido' : 'Nenhum logo escolhido',
      limitLabel: null,
      limitReached: false,
      confirmLabel: 'Usar como logo',
      canConfirm: count === 1,
    };

  const d = s.descricao ?? {};
  const alvoDaDescricao = d.itemId ? porId.get(d.itemId) : null;
  const valor = d.valor ?? '';
  const describeDialog = {
    open: !!d.aberta,
    itemId: d.itemId ?? null,
    thumbnailUrl: alvoDaDescricao?.miniatura ?? null,
    value: valor,
    maxLength: MAX_DESCRICAO,
    counterLabel: `${valor.length}/${MAX_DESCRICAO}`,
    helper: 'Descreva o que aparece na foto para quem usa leitor de tela. Ex.: "Cadeira de barbeiro antiga, de couro".',
    saving: !!d.salvando,
    errorMessage: d.erro ?? null,
  };

  const a = s.apagar ?? {};
  const alvoDoApagar = a.itemId ? porId.get(a.itemId) : null;
  const emUso = Number(a.emUso ?? alvoDoApagar?.usadoEm) || 0;
  const deleteDialog = {
    open: !!a.aberto,
    itemId: a.itemId ?? null,
    thumbnailUrl: alvoDoApagar?.miniatura ?? null,
    title: 'Apagar esta foto?',
    message: emUso > 0
      ? `Esta foto está em ${emUso} ${plural(emUso, 'página', 'páginas')}. Se apagar, ela some ${plural(emUso, 'dela', 'delas')} também.`
      : 'Ela sai da sua biblioteca. Nenhuma página usa esta foto.',
    inUse: emUso > 0,
    confirmLabel: emUso > 0 ? 'Apagar mesmo assim' : 'Apagar',
    deleting: !!a.apagando,
    errorMessage: a.erro ?? null,
  };

  const vazio = itens.length === 0;
  return {
    open: !!s.open,
    mode: galeria ? 'gallery' : 'logo',
    title: galeria ? 'Escolher fotos' : 'Escolher o logo',
    subtitle: galeria ? 'Toque nas fotos na ordem em que devem aparecer.' : 'Use uma imagem sua: o logo é a sua marca.',
    state: s.carregando && vazio ? 'loading' : s.erro && vazio ? 'error' : 'ready',
    errorMessage: s.erro && vazio ? (s.erro.mensagem || 'A biblioteca de fotos está indisponível agora. Tente de novo em instantes.') : null,
    quota: cota,
    upload: {
      canUpload: cota.level !== 'full',
      blockedReason: cota.level === 'full'
        ? `Sua biblioteca está cheia (${cota.used} de ${cota.limit} fotos). Apague alguma ou mude de plano.` : null,
      accept: 'image/jpeg,image/png,image/webp',
      acceptLabel: 'JPG, PNG ou WebP · até 15 MB',
      buttonLabel: galeria ? 'Enviar fotos' : 'Enviar foto',
      dropLabel: 'ou arraste para cá',
    },
    uploads: (s.envios ?? []).map(montarEnvio),
    sections,
    hasMore: !!s.proximo,
    loadingMore: !!s.carregandoMais,
    selection,
    describeDialog,
    deleteDialog,
    notice: s.aviso ?? null,
    fullScreen: !!s.telaCheia,
  };
}

/**
 * Item provisório da foto que ACABOU de subir: a Mídia só a lista depois que o FileService avisa pela fila (alguns
 * segundos), e quem envia quer escolher na hora. Montado SÓ com o que o FileService devolveu no 201 (`ArquivoV2`) —
 * nada derivado: as medidas, a cor e os endereços são os da foto guardada.
 */
/**
 * O aviso efêmero da biblioteca (`vm.notice`) no formato do `AppSnackbar` da casa — quem desenha é o container
 * (`MediaLibrary.jsx`), não a View. `ultimo` = o aviso anterior: mantém o texto enquanto o aviso some (sem piscar vazio).
 */
export function snackbarDoAviso(aviso, ultimo = null) {
  const mostrado = aviso ?? ultimo;
  return { open: !!aviso, message: mostrado?.message ?? '', severity: mostrado?.tone ?? 'info' };
}

export function itemDoEnvio({ assetId, arquivo, nome }) {
  return {
    assetId,
    origem: MediaOrigin.Owner,
    w: arquivo?.largura ?? 0,
    h: arquivo?.altura ?? 0,
    color: arquivo?.cor ?? null,
    alt: null,
    nome: nome ?? null,
    credito: null,
    criadoEm: null,
    usadoEm: 0,
    miniatura: arquivo?.enderecos?.w320 ?? '',
    previa: arquivo?.enderecos?.w1024 ?? arquivo?.enderecos?.w320 ?? '',
  };
}
